import { reviewThemeNames } from "@domains/reviews/shared/reviewThemes.ts";
import { goalsFr } from "@domains/goals/client/i18n/fr.ts";
import { goodVibesFr, goodVibeCardFr, goodVibeDetailDialogFr } from "@domains/good-vibes/client/i18n/fr.ts";
import type { en } from "./en.ts";

export const fr: typeof en = {
    ...goalsFr,
    ...goodVibesFr,
    Navigation: {
        team: {
            label: "Équipe",
            description: "Aperçus et rapports d'équipe",
            main: {
                label: "Équipe",
                overview: { label: "Vue d'ensemble" },
                engagement: { label: "Engagement" },
                meetings: { label: "Réunions" },
                "good-vibes": { label: "Bonnes Vibes" },
                feedback: { label: "Feedback" },
                reviews: { label: "Évaluations" },
                goals: { label: "Objectifs" }
            }
        },
        "team-reviews-detail": { label: "Évaluation" },
        "team-reviews-write": { label: "Rédiger l'évaluation" },
        "team-reviews-share": { label: "Partager l'évaluation" },
        "team-goals-detail": { label: "Objectif" },
        "team-goals-edit": { label: "Modifier l'objectif" },
        "team-engagement-detail": { label: "Métrique" }
    },
    Errors: {
        retry: "Réessayer",
        retrying: "Nouvel essai…",
        reassurance: "Veuillez réessayer plus tard.",
        refreshBanner: "Impossible d'actualiser — affichage des derniers résultats."
    },
    TeamOverviewPage: {
        error: "Impossible de charger la vue d'ensemble de l'équipe pour l'instant.",
        widgetError: {
            engagement: "Impossible de charger Engagement pour l'instant.",
            goals: "Impossible de charger les Objectifs pour l'instant.",
            feedback: "Impossible de charger le feedback pour l'instant.",
            reviews: "Impossible de charger les Évaluations de performance pour l'instant.",
            goodVibes: "Impossible de charger les Bonnes Vibes pour l'instant."
        },
        segmentUnsupported: {
            engagement: "L'engagement ne prend pas encore en charge le filtrage avancé.",
            feedback: "Le feedback ne prend pas encore en charge le filtrage avancé.",
            goals: "Ce résumé ne reflète pas encore le filtre appliqué. Ouvrez l'onglet Objectifs pour le voir."
        },
        feedbackAudienceRefusal: {
            segmentNotVisible: "Vous n'avez plus accès à ce segment, le feedback ne peut donc pas y être restreint.",
            handPickedSegment: "Le feedback ne peut être restreint qu'aux segments basés sur des règles, et celui-ci est composé manuellement.",
            brokenRules: "Certaines règles de ce segment ne sont pas prises en charge, le feedback ne peut donc pas y être restreint.",
            unsupportedCapability: "Le feedback ne peut pas être restreint à un segment qui correspond à n'importe quelle règle."
        },
        liveRegion: {
            loading: "Chargement de la vue d'ensemble de l'équipe"
        }
    },
    TeamGoalsWidget: {
        title: "Objectifs",
        viewAll: "Voir tout",
        viewAllAriaLabel: "Voir tous les objectifs",
        viewStatusGoals: "Voir les objectifs {{status}}",
        averageProgress: "Progression moyenne",
        onTrack: "Bien avancé",
        atRisk: "À surveiller",
        offTrack: "En difficulté",
        noStatus: "Aucun statut",
        totalCount_one: "{{count}} objectif",
        totalCount_other: "{{count}} objectifs"
    },
    TeamFeedbackWidget: {
        title: "Feedback",
        viewAll: "Voir tout",
        viewAllAriaLabel: "Voir tout le feedback",
        unreadMessages_one: "feedback non lu",
        unreadMessages_other: "feedbacks non lus",
        unrepliedMessages_one: "message sans réponse",
        unrepliedMessages_other: "messages sans réponse",
        period: "30 derniers jours"
    },
    EngagementRating: {
        critical: "Critique",
        veryLow: "Très faible",
        low: "Faible",
        good: "Bon",
        veryGood: "Très bon",
        great: "Excellent",
        amazing: "Exceptionnel"
    },
    TeamEngagementWidget: {
        title: "Engagement",
        viewAll: "Voir tout",
        viewAllAriaLabel: "Voir tout l'engagement",
        score: "Score",
        enps: "eNPS",
        band: {
            "needs-work": "À améliorer",
            neutral: "Neutre",
            good: "Bon",
            excellent: "Excellent"
        },
        delta_one: "{{sign}}{{value}} pt",
        delta_other: "{{sign}}{{value}} pts",
        footer_one: "30 derniers jours · Basé sur {{rate}} % de participation · {{count}} sur {{invited}} a répondu",
        footer_other: "30 derniers jours · Basé sur {{rate}} % de participation · {{count}} sur {{invited}} ont répondu",
        scoreAriaLabel: "Score {{value}} sur 10",
        enpsAriaLabel: "eNPS {{value}} sur une échelle de -100 à +100",
        noData: "Aucune donnée"
    },
    TeamPerformanceReviewsWidget: {
        title: "Évaluations de performance",
        viewAll: "Voir tout",
        viewAllAriaLabel: "Voir toutes les évaluations",
        averageScore: "Score moyen",
        scoreScale: "/ {{scale}}",
        scoreDistribution: "Distribution des scores",
        noRatings: "Aucune note disponible pour ce cycle",
        positiveSentiments: "Sentiments positifs",
        positiveSentimentsEmpty: "Aucun thème positif détecté.",
        negativeSentiments: "Sentiments négatifs",
        negativeSentimentsEmpty: "Aucune préoccupation détectée.",
        distributionRow_one: "{{count}} évaluation notée {{score}} sur {{scale}}",
        distributionRow_other: "{{count}} évaluations notées {{score}} sur {{scale}}",
        footer_one: "{{count}} personne évaluée · Selon la plus récente évaluation de chaque personne",
        footer_other: "{{count}} personnes évaluées · Selon la plus récente évaluation de chaque personne",
        themes: reviewThemeNames("fr-CA")
    },
    TeamOrgChartWidget: {
        peopleCount_one: "{{count}} personne",
        peopleCount_other: "{{count}} personnes",
        subtext: "Dirigée par <strong>vous</strong>. Les indicateurs d'équipe excluent le responsable.",
        viewInOrgChart: "Voir dans l'organigramme",
        orgChartModal: {
            eyebrow: "À noter",
            title: "Un petit détour",
            body: "L'organigramme complet n'est pas encore disponible dans la nouvelle application. Nous vous redirigerons vers Pingboard pour le consulter.",
            cancel: "Annuler",
            continue: "Continuer dans Pingboard"
        },
        youBadge: "Vous",
        youSuffix: "{{name}} (vous)",
        unknownMember: "Membre inconnu",
        viewMoreInOrgChart_one: "Voir {{count}} de plus dans l'organigramme",
        viewMoreInOrgChart_other: "Voir {{count}} de plus dans l'organigramme",
        tooltipMore: "et {{count}} de plus",
        empty: {
            title: "Vous n'êtes pas encore sur l'organigramme",
            description: "Une fois que vous serez placé sur l'organigramme, votre équipe apparaîtra ici."
        },
        error: "Nous n'avons pas pu charger l'organigramme.",
        liveRegion: {
            loading: "Chargement de l'organigramme"
        }
    },
    TeamCompanyOrgChartWidget: {
        label: "Organigramme",
        viewInOrgChart: "Voir l'organigramme",
        treeLabel: "Organigramme de l'entreprise",
        unknownMember: "Membre inconnu",
        youBadge: "Vous",
        youSuffix: "{{name}} (vous)",
        viewMore: "Voir {{count}} de plus dans l'organigramme",
        tooltipMore: "et {{count}} de plus",
        liveRegion: {
            loading: "Chargement de l'organigramme"
        },
        empty: {
            title: "Personne ne figure encore dans l'organigramme."
        },
        error: {
            title: "Impossible de charger l'organigramme.",
            description: "Veuillez réessayer plus tard.",
            retry: "Réessayer",
            retrying: "Nouvelle tentative"
        },
        orgChartModal: {
            eyebrow: "À noter",
            title: "Un petit détour",
            body: "L'organigramme complet n'est pas encore disponible dans la nouvelle application. Nous vous redirigerons vers Pingboard pour le consulter.",
            cancel: "Annuler",
            continue: "Continuer dans Pingboard"
        }
    },
    TeamFilteredOrgChartWidget: {
        peopleCount_one: "{{count}} personne",
        peopleCount_other: "{{count}} personnes",
        subtext: "Toutes les personnes correspondant au filtre appliqué.",
        viewInOrgChart: "Voir dans l'organigramme",
        facepileLabel: "Personnes correspondantes",
        unknownMember: "Membre inconnu",
        youBadge: "Vous",
        youSuffix: "{{name}} (vous)",
        viewMore: "Voir {{count}} de plus dans l'organigramme",
        tooltipMore: "et {{count}} de plus",
        liveRegion: {
            loading: "Chargement de l'organigramme"
        },
        empty: {
            title: "Personne ne correspond à ce filtre",
            description: "Modifiez ou effacez le filtre pour voir des personnes ici."
        },
        error: "Impossible de charger l'organigramme.",
        orgChartModal: {
            eyebrow: "Avertissement",
            title: "Un petit détour",
            body: "L'organigramme complet n'est pas encore disponible dans la nouvelle application. Nous vous dirigerons vers Pingboard pour le consulter.",
            cancel: "Annuler",
            continue: "Continuer dans Pingboard"
        }
    },
    TeamReportingLineOrgChartWidget: {
        peopleCount_one: "{{count}} personne",
        peopleCount_other: "{{count}} personnes",
        subtext: "Dirigée par <strong>vous</strong>. Les indicateurs d'équipe excluent le responsable.",
        viewInOrgChart: "Voir dans l'organigramme",
        treeLabel: "Tous les subordonnés",
        youBadge: "Vous",
        youSuffix: "{{name}} (vous)",
        unknownMember: "Membre inconnu",
        viewMore: "Voir {{count}} de plus dans l'organigramme",
        tooltipMore: "et {{count}} de plus",
        liveRegion: {
            loading: "Chargement de l'organigramme"
        },
        empty: {
            title: "Vous n'êtes pas encore sur l'organigramme",
            description: "Une fois que vous serez placé sur l'organigramme, votre équipe apparaîtra ici."
        },
        error: "Impossible de charger l'organigramme.",
        orgChartModal: {
            eyebrow: "À noter",
            title: "Un petit détour",
            body: "L'organigramme complet n'est pas encore disponible dans la nouvelle application. Nous vous redirigerons vers Pingboard pour le consulter.",
            cancel: "Annuler",
            continue: "Continuer dans Pingboard"
        }
    },
    TeamEngagementPage: {
        title: "Engagement",
        sectionError: {
            scores: "Impossible de charger les scores d'engagement pour l'instant.",
            enps: "Impossible de charger l'eNPS pour l'instant."
        },
        segmentUnsupported: {
            title: "Le filtrage avancé n'est pas encore pris en charge ici",
            description: "L'engagement ne prend pas encore en charge le filtrage avancé. Passez à Mon équipe ou Tout le monde pour voir cet onglet."
        },
        heatmapLink: {
            title: "Carte thermique",
            description: "Comparez l'engagement entre les départements dans le rapport complet.",
            viewHeatmap: "Voir la carte thermique"
        },
        liveRegion: {
            loading: "Chargement des données d'engagement"
        },
        timeFrame: {
            ariaLabel: "Période",
            clearAriaLabel: "Effacer le filtre de période",
            "7d": "7 derniers jours",
            "30d": "30 derniers jours",
            "3m": "3 derniers mois",
            "6m": "6 derniers mois",
            quarter: "Ce trimestre",
            year: "Cette année",
            allTime: "Depuis toujours",
            relativeLabel: {
                "7d": "7 jours",
                "30d": "30 jours",
                "3m": "3 mois",
                "6m": "6 mois",
                quarter: "ce trimestre",
                year: "cette année",
                allTime: "depuis toujours"
            }
        }
    },
    OverallScoreCard: {
        title: "Dernier score d'engagement",
        subtitle: "Les derniers scores sont toujours calculés à partir des réponses reçues dans une fenêtre de 90 jours suivant la période sélectionnée.",
        delta_one: "{{sign}}{{value}} pt vs {{period}} précédents",
        delta_other: "{{sign}}{{value}} pts vs {{period}} précédents",
        deltaZero: "Aucun changement vs {{period}} précédents",
        noData: "Non disponible",
        notEnoughResponses: "Pas assez de réponses",
        notEnoughResponsesAnonymity: "Moins de 3 réponses ont été recueillies durant cette période. Les résultats sont masqués pour protéger l'anonymat.",
        scoreAriaLabel: "Score d'engagement",
        scoreNotAvailableAriaLabel: "Score d'engagement non disponible : pas assez de réponses",
        outOf: "sur {{max}}",
        valueLabel: "{{score}} sur {{max}}",
        yoy: {
            label: "Votre équipe, à la même période l'année dernière",
            labelOrganization: "Entreprise, à la même période l'année dernière",
            availableFrom: "Disponible en {{date}}",
            delta_one: "{{sign}}{{value}} pt",
            delta_other: "{{sign}}{{value}} pts"
        },
        enps: {
            label: "eNPS des employés",
            delta_one: "{{sign}}{{value}} pt",
            delta_other: "{{sign}}{{value}} pts"
        },
        participation: {
            label: "Taux de participation",
            delta_one: "{{sign}}{{value}} pt",
            delta_other: "{{sign}}{{value}} pts"
        },
        sparkline: {
            yourTeam: "Votre équipe",
            company: "Entreprise"
        }
    },
    MetricsAndDrivers: {
        title: "Métriques et facteurs",
        description: "Les métriques qui ont le plus changé pendant cette période.",
        notEnoughResponses: "Pas assez de réponses",
        notEnoughResponsesAnonymity: "Moins de 3 réponses ont été recueillies durant cette période. Les résultats sont masqués pour protéger l'anonymat.",
        topDecreases: "Plus fortes baisses",
        topGains: "Plus fortes hausses",
        showAll: "Afficher toutes les métriques",
        hideAll: "Masquer toutes les métriques",
        delta_one: "{{sign}}{{value}} pt",
        delta_other: "{{sign}}{{value}} pts",
        moverLinkAriaLabel: "Voir les résultats des questions de {{label}}",
        noPriorPeriodTitle: "Pas encore assez d'historique",
        noPriorPeriod: "Les variations des métriques apparaîtront une fois que votre équipe aura une période complète à comparer.",
        noDecreases: "Aucune baisse notable cette période.",
        noGains: "Aucune hausse notable cette période.",
        tooltipLabel: "À propos de {{metric}}",
        table: {
            ariaLabel: "Métriques d'engagement",
            noDelta: "Aucune comparaison disponible",
            columns: {
                metric: "Métrique",
                score: "Score",
                changeOver: "Changement sur {{period}}",
                details: "Ouvrir les détails de la métrique"
            },
            liveRegion: {
                sortChanged: "Trié par {{column}} {{direction}}",
                ascending: "ordre croissant",
                descending: "ordre décroissant",
                columns: {
                    metric: "Métrique",
                    score: "Score",
                    change: "Changement"
                }
            }
        }
    },
    EnpsSection: {
        title: "Score Net Promoteur des employés",
        intro: "Les scores reflètent les 90 derniers jours de réponses, indépendamment de la plage de dates sélectionnée ci-dessus.",
        tooltip: "Les employés évaluent leur probabilité de recommander ce lieu de travail sur une échelle de 0 à 10. Les promoteurs obtiennent 9–10, les passifs 7–8, les détracteurs 0–6.",
        tooltipLabel: "À propos de l'eNPS",
        learnMore: "En savoir plus",
        offBanner: "Les sondages eNPS sont désactivés. Affichage du dernier score du {{date}}.",
        offBannerDateless: "Les sondages eNPS sont désactivés. Affichage du dernier score disponible.",
        offBannerNoScore: "Les sondages eNPS sont désactivés pour votre organisation.",
        showDistribution: "Afficher la distribution",
        hideDistribution: "Masquer la distribution",
        notEnoughResponsesAnonymity: "Moins de 3 réponses ont été recueillies durant cette période. Les résultats sont masqués pour protéger l'anonymat.",
        legend: {
            yourTeam: "Votre équipe",
            company: "Entreprise"
        }
    },
    TeamGoodVibesPage: {
        error: "Impossible de charger les Bonnes Vibes pour l'instant.",
        listAriaLabel: "Bonnes Vibes",
        liveRegion: {
            loading: "Chargement des Bonnes Vibes",
            loadingMore: "Chargement d'autres Bonnes Vibes",
            loadedMore_one: "{{count}} Bonne Vibe supplémentaire chargée",
            loadedMore_other: "{{count}} Bonnes Vibes supplémentaires chargées"
        },
        empty: "Aucune Bonne Vibe ne correspond à ces filtres.",
        emptyTimeframe: {
            "7d": "Aucune Bonne Vibe au cours des 7 derniers jours.",
            "30d": "Aucune Bonne Vibe au cours des 30 derniers jours.",
            "3m": "Aucune Bonne Vibe au cours des 3 derniers mois.",
            "6m": "Aucune Bonne Vibe au cours des 6 derniers mois.",
            quarter: "Aucune Bonne Vibe ce trimestre.",
            year: "Aucune Bonne Vibe cette année."
        },
        emptyAllTime: "Aucune Bonne Vibe pour le moment.",
        giveModal: {
            eyebrow: "À noter",
            title: "Un petit détour",
            body: "Cette fonctionnalité n'est pas encore disponible dans la nouvelle application. Nous vous redirigerons vers Officevibe pour la compléter. Rien ne sera perdu.",
            cancel: "Annuler",
            continue: "Continuer dans Officevibe"
        },
        composeDialog: {
            title: "Envoyer des Bonnes Vibes",
            recipientsLabel: "À",
            recipientsPlaceholder: "Ajouter quelqu'un",
            recipientsSearchPlaceholder: "Rechercher des personnes",
            recipientsSelectedLabel: "Destinataires sélectionnés",
            recipientsEmpty: "Aucun résultat",
            recipientsLoading: "Recherche en cours…",
            subjectLabel: "Sujet",
            subjectPlaceholder: "Qu'ont-ils fait ?",
            writeOwnLabel: "Écrire le vôtre, ou choisir un sujet",
            useTypedLabel: 'Utiliser "{{text}}"',
            customCategoryLabel: "Personnalisé",
            changeSubjectLabel: "Changer",
            recipientsRemoveLabel: "Retirer {{name}}",
            messageLabel: "Message",
            messagePlaceholder: "Dites ce qu'ils ont fait et pourquoi c'était important.",
            messageCharactersLeft: "{{count}} caractères restants",
            audienceLabel: "Audience",
            directLabel: "Direct",
            directDescription: "Seulement les destinataires, leurs gestionnaires et leurs supérieurs peuvent voir ceci.",
            publicLabel: "Public",
            publicDescription: "Tous les membres de l'entreprise peuvent voir ceci.",
            fromLabel: "De {{name}}",
            cancelLabel: "Annuler",
            submitDirectLabel: "Envoyer directement",
            submitPublicLabel: "Envoyer publiquement",
            discardChangesTitle: "Abandonner cette Bonne Vibe ?",
            discardChangesBody: "Vous n'avez pas encore envoyé ceci. Quitter maintenant fera perdre ce que vous avez écrit.",
            discardChangesConfirmLabel: "Abandonner",
            discardChangesCancelLabel: "Continuer à modifier",
            successMessage: "Vous venez d'ensoleiller la journée de quelqu'un !",
            successCloseLabel: "Fermer",
            mostUsedThisMonthLabel: "Les plus utilisées ce mois-ci",
            usageCountLabel: "{{count}} utilisations ce mois-ci"
        },
        giveError: "Impossible d'envoyer. Veuillez réessayer.",
        visibility: {
            public: "Publique",
            direct: "Directe"
        },
        sealed: {
            ariaLabel: "Nouvelle Bonne Vibe pour {{recipients}}. Appuyez et maintenez pour l'ouvrir.",
            badge: "Nouvelle Bonne Vibe",
            pressAndHold: "Appuyez et maintenez pour l'ouvrir"
        }
    },
    GoodVibesToolbar: {
        audience: {
            label: "Audience",
            triggerPrefix: "Audience :",
            clearAriaLabel: "Effacer le filtre d'audience",
            options: {
                all: "Tous",
                public: "Public",
                direct: "Direct"
            }
        },
        direction: {
            groupLabel: "Direction",
            triggerPrefix: "Direction :",
            clearAriaLabel: "Effacer le filtre de direction",
            options: {
                all: "Tous",
                received: "Reçus",
                given: "Envoyés"
            }
        },
        themes: {
            label: "Thèmes",
            ariaLabel: "Filtrer par thème",
            clearFilter: "Effacer le filtre"
        },
        timeframe: {
            ariaLabel: "Période",
            clearAriaLabel: "Effacer le filtre de période",
            options: {
                "7d": "7 derniers jours",
                "30d": "30 derniers jours",
                "3m": "3 derniers mois",
                "6m": "6 derniers mois",
                quarter: "Ce trimestre",
                year: "Cette année",
                all: "Depuis toujours"
            }
        },
        resetFilters: "Effacer les filtres",
        sendInClassic: "Envoyer des Bonnes Vibes",
        sendGoodVibes: "Envoyer des Good Vibes"
    },
    GoodVibesCard: { ...goodVibeCardFr },
    GoodVibesDetailDialog: { ...goodVibeDetailDialogFr },
    TeamGoodVibesWidget: {
        title: "Bonnes Vibes",
        viewAll: "Voir tout",
        viewAllAriaLabel: "Voir toutes les Bonnes Vibes",
        topRecipients: "Principaux destinataires",
        noRecipients: "Aucune reconnaissance au cours des 30 derniers jours.",
        recognitionCount_one: "{{count}}",
        recognitionCount_other: "{{count}}",
        summary: "30 derniers jours · {{count}} sur {{total}} reconnus"
    },
    TeamFeedbackPage: {
        error: "Impossible de charger le feedback de l'équipe pour l'instant.",
        empty: {
            headline: "Aucun feedback en attente.",
            subtext: "Les nouveaux messages de votre équipe apparaîtront ici."
        },
        filteredEmpty: {
            headline: "Aucun feedback ne correspond à ces filtres",
            subtext: "Essayez d'effacer vos filtres ou votre recherche."
        },
        segmentUnsupported: {
            title: "Le filtrage avancé n'est pas encore pris en charge ici",
            description: "Le feedback ne prend pas encore en charge le filtrage avancé. Passez à Mon équipe ou Tout le monde pour voir cet onglet."
        },
        audienceRefusal: {
            segmentNotVisible: {
                title: "Ce segment ne vous est plus accessible",
                description: "Vous n'avez plus accès à ce segment. Choisissez-en un autre ou retirez le filtre pour voir le feedback."
            },
            handPickedSegment: {
                title: "Les segments composés manuellement ne sont pas pris en charge ici",
                description: "Le feedback ne peut être restreint qu'aux segments basés sur des règles. Choisissez un segment basé sur des règles ou retirez le filtre."
            },
            brokenRules: {
                title: "Ce segment contient des règles que nous ne pouvons pas lire",
                description: "Certaines règles de ce segment ne sont pas prises en charge, le feedback ne peut donc pas y être restreint. Modifiez le segment ou retirez le filtre."
            },
            unsupportedCapability: {
                title: "Ce segment n'est pas encore pris en charge ici",
                description: "Le feedback ne peut pas être restreint à un segment qui correspond à n'importe quelle règle. Passez-le à toutes les règles."
            }
        },
        liveRegion: {
            loading: "Chargement du feedback de l'équipe",
            loadingMoreAnnouncement: "Chargement de plus de feedback",
            loadedMore_one: "{{count}} feedback supplémentaire chargé",
            loadedMore_other: "{{count}} feedbacks supplémentaires chargés",
            results_one: "{{count}} résultat",
            results_other: "{{count}} résultats"
        },
        ariaLabel: "Feedback de l'équipe",
        loadingMore: "Chargement en cours",
        unread: "Non lu",
        moreCollapsed_one: "+{{count}} autre",
        moreCollapsed_other: "+{{count}} autres",
        toMonitor: "À surveiller",
        toMonitorTooltip: "Feedback à surveiller signalé par {{category}}",
        status: {
            awaiting: "En attente de réponse"
        },
        totalCount_one: "{{count}} feedback",
        totalCount_other: "{{count}} feedbacks",
        sender: {
            anonymous: "Anonyme"
        },
        typeFilter: {
            label: "Type de feedback",
            menuAriaLabel: "Filtrer par type de feedback",
            clearFilter: "Effacer le filtre de type de feedback",
            dismissAriaLabel: "Retirer le filtre de type de feedback",
            triggerLabel: "Type de feedback · {{count}}",
            triggerLabelSingle: "Type de feedback : {{label}}"
        }
    },
    FeedbackToolbar: {
        searchPlaceholder: "Rechercher du feedback",
        searchAriaLabel: "Rechercher du feedback",
        filters: {
            unreplied: "Tous les sans réponse",
            unread: "Tous les non lus",
            all: "Tous les feedbacks",
            ariaLabel: "Filtrer les feedbacks",
            clearAriaLabel: "Effacer le filtre de feedback"
        },
        timeFrame: {
            ariaLabel: "Période",
            clearAriaLabel: "Effacer le filtre de période",
            "7d": "7 derniers jours",
            "30d": "30 derniers jours",
            "3m": "3 derniers mois",
            "6m": "6 derniers mois",
            quarter: "Ce trimestre",
            year: "Cette année"
        },
        addFilter: {
            label: "Ajouter un filtre",
            menuAriaLabel: "Afficher ou masquer les filtres"
        },
        resetFilters: "Effacer les filtres",
        fullscreen: {
            expand: "Agrandir en plein écran",
            collapse: "Fermer le plein écran"
        }
    },
    SummarizeFeedbackButton: {
        button: "Résumer tout le feedback",
        pending: "Collecte du feedback…",
        error: "Impossible de résumer le feedback. Veuillez réessayer.",
        promptReportingLine: "Résumer le feedback de mes collaborateurs",
        promptDirectReports: "Résumer le feedback de mes collaborateurs directs",
        promptOrganization: "Résumer le feedback de mon organisation"
    },
    TeamFeedbackDetail: {
        error: "Impossible de charger ce feedback pour l'instant.",
        liveRegion: {
            loading: "Chargement du feedback"
        },
        toMonitor: "À surveiller",
        toMonitorTooltip: "Feedback à surveiller signalé par {{category}}",
        toMonitorBanner: {
            label: "Signalé à surveiller",
            hide: "Masquer",
            why: "Pourquoi ?",
            accuracyPrompt: "Ce signalement était-il exact ?",
            accurate: "Indiquer que ce signalement est exact",
            inaccurate: "Indiquer que ce signalement est inexact",
            thanks: "Merci pour votre commentaire.",
            reasonDialog: {
                title: "Qu'avons-nous mal évalué ?",
                description: "Votre commentaire aide à ajuster les critères de signalement à surveiller. Facultatif.",
                placeholder: "Ce signalement était erroné parce que...",
                skip: "Ignorer",
                submit: "Envoyer le commentaire"
            }
        },
        removeToMonitorFlag: {
            button: "Retirer l'indicateur à surveiller",
            dialog: {
                title: "Retirer l'indicateur ?",
                description: "L'indicateur à surveiller sera retiré de ce feedback. Cette action est irréversible.",
                cancel: "Annuler",
                confirm: "Retirer l'indicateur"
            },
            success: "Indicateur à surveiller retiré.",
            error: "Impossible de retirer l'indicateur à surveiller. Veuillez réessayer."
        },
        status: {
            awaiting: "En attente de réponse"
        },
        sender: {
            anonymous: "Anonyme"
        },
        closestReportingManager: {
            from: "de l'équipe de {{name}}",
            unavailable: "(Gestionnaire non disponible)"
        },
        questionSenderLabel: "Workleap",
        details: {
            header: "Détails",
            sentiment: "Sentiment",
            metric: "Métrique",
            status: "Statut",
            feedbackType: "Type de feedback",
            topics: "Sujets",
            collapse: "Réduire les détails",
            expand: "Afficher les détails"
        },
        emptyReadingPane: "Sélectionnez un message à gauche pour le lire et y répondre. Les auteurs restent anonymes à moins de choisir de signer leur message."
    },
    FeedbackReplyComposer: {
        textareaLabel: "Répondre au feedback",
        placeholder: "Répondre…",
        send: "Envoyer la réponse",
        sending: "Envoi de la réponse…",
        successToast: "Réponse envoyée",
        errorToast: "Impossible d'envoyer votre réponse. Veuillez réessayer.",
        helpMeReply: "Aidez-moi à répondre",
        undo: "Annuler",
        generating: "Lecture de la conversation…",
        generatingLiveRegion: "Génération d'une réponse",
        generateErrorToast: "Une suggestion de réponse n'a pas pu être générée. Veuillez réessayer.",
        improve: "Améliorer",
        improveLessFormal: "Moins formel",
        improveMoreConcise: "Plus concis",
        improveMoreNeutral: "Plus neutre",
        improveMoreSimple: "Plus simple",
        improveMoreActionable: "Plus concret",
        improveMoreAppreciative: "Plus reconnaissant",
        improveStartOver: "Recommencer"
    },
    ManageReviewCyclesLink: {
        label: "Gérer tous les cycles d'évaluation dans Workleap Admin"
    },
    TeamReviewsPage: {
        error: "Impossible de charger les évaluations de l'équipe pour l'instant.",
        noResults: "Aucune évaluation trouvée",
        noResultsHint: "Essayez d'ajuster les filtres.",
        empty: {
            heading: "Aucune évaluation pour l'instant",
            hint: "Les évaluations de performance de votre équipe apparaîtront ici dès qu'un cycle d'évaluation sera en cours."
        },
        totalCount_one: "{{count}} évaluation",
        totalCount_other: "{{count}} évaluations",
        liveRegion: {
            loading: "Chargement des évaluations",
            loadingMoreAnnouncement: "Chargement d'autres évaluations",
            loadedMore_one: "{{count}} résultat supplémentaire chargé",
            loadedMore_other: "{{count}} résultats supplémentaires chargés",
            results_one: "{{count}} résultat",
            results_other: "{{count}} résultats"
        }
    },
    ReviewsToolbar: {
        searchPlaceholder: "Rechercher des personnes évaluées",
        searchAriaLabel: "Rechercher des personnes évaluées",
        scope: {
            ariaLabel: "Période",
            latest: "Dernière évaluation",
            past90: "90 derniers jours",
            pastYear: "Dernière année",
            allTime: "Toutes les périodes"
        },
        cycleStatus: {
            ariaLabel: "Statut du cycle",
            all: "Tous les cycles",
            active: "Cycles actifs",
            locked: "Cycles verrouillés"
        },
        sharing: {
            ariaLabel: "Partage",
            all: "Tous les partages",
            notShared: "Non partagée",
            shared: "Partagée",
            acknowledged: "Pris connaissance"
        },
        clearScope: "Effacer le filtre de période",
        clearCycleStatus: "Effacer le filtre de cycle",
        clearSharing: "Effacer le filtre de partage",
        resetFilters: "Effacer les filtres"
    },
    ReviewsTable: {
        ariaLabel: "Évaluations de l'équipe",
        columns: {
            review: "Évaluation",
            score: "Score",
            period: "Période",
            types: "Types",
            sharing: "Partage"
        },
        expandRow: "Afficher les évaluateurs de {{reviewee}}",
        collapseRow: "Masquer les évaluateurs de {{reviewee}}",
        drawerRow: "Évaluateurs de {{reviewee}}",
        noReviewsGroup: "Aucune évaluation ({{count}})"
    },
    ReviewRow: {
        noReviewsYet: "Aucune évaluation pour l'instant",
        mobileLabels: {
            score: "Score de l'évaluation",
            period: "Période d'évaluation",
            types: "Types d'évaluation",
            sharing: "Partage"
        },
        types: {
            self: "Auto",
            manager: "Gestionnaire",
            peer: "Pair",
            upward: "Ascendante"
        },
        sharing: {
            notShared: "Non partagée",
            shared: "Partagée",
            acknowledged: "Pris connaissance"
        }
    },
    ReviewLotDrawer: {
        liveRegion: {
            loading: "Chargement des évaluateurs"
        },
        error: "Impossible de charger les évaluateurs.",
        retry: "Réessayer",
        empty: "Aucun évaluateur pour cette évaluation."
    },
    LotReviewerRow: {
        roles: {
            self: "Auto",
            manager: "Gestionnaire",
            peer: "Pair",
            upward: "Ascendante"
        },
        status: {
            notStarted: "Non commencée",
            inProgress: "En cours",
            submitted: "Soumise",
            overdue: "En retard"
        },
        submittedOn: "le {{date}}",
        dueOn: "Échéance {{date}}",
        anonymous: {
            peer: "Pair anonyme",
            upward: "Évaluateur ascendant anonyme",
            reviewer: "Évaluateur anonyme"
        },
        noDirectManager: "Pas de gestionnaire immédiat",
        deletedMember: "Membre supprimé"
    },
    TeamMeetingsPage: {
        notConnectedBody: "Connectez votre calendrier pour que l’agent puisse voir vos disponibilités, planifier vos 1:1 récurrents et rédiger un ordre du jour documenté avant chaque réunion.",
        connectCalendar: "Connecter un calendrier",
        expiredBody: "La connexion à votre calendrier a expiré. Reconnectez-le et l’agent pourra de nouveau planifier vos 1:1 et rédiger un ordre du jour avant chaque réunion.",
        reconnectCalendar: "Reconnecter un calendrier",
        organizeMeetings: "Planifier des réunions",
        error: "Nous n’avons pas pu charger vos réunions pour le moment.",
        noDataTitle: "Aucune réunion planifiée",
        noData: "Votre calendrier est connecté, mais aucun 1:1 récurrent n’est encore planifié. Organisez vos réunions et l’agent les planifiera, puis rédigera un ordre du jour documenté avant chacune.",
        noResults: "Aucune réunion ne correspond à vos filtres.",
        clearFilters: "Effacer les filtres",
        totalCount_one: "{{count}} réunion",
        totalCount_other: "{{count}} réunions",
        liveRegion: {
            sortChanged: "Trié par date {{direction}}",
            ascending: "ordre croissant",
            descending: "ordre décroissant",
            loading: "Chargement des réunions",
            loadingMoreAnnouncement: "Chargement de réunions supplémentaires",
            loadedMore_one: "{{count}} réunion supplémentaire chargée",
            loadedMore_other: "{{count}} réunions supplémentaires chargées",
            results_one: "{{count}} réunion",
            results_other: "{{count}} réunions"
        }
    },
    TeamGoalsPage: {
        create: {
            newGoal: "Nouvel objectif",
            error: "Erreur lors de la création de l’objectif"
        },
        error: "Impossible de charger les objectifs de l'équipe pour l'instant.",
        empty: {
            heading: "Aucun objectif pour l'instant",
            hint: "Les objectifs de l'équipe apparaîtront ici dès qu'un objectif sera créé."
        },
        noResults: "Aucun objectif trouvé",
        noResultsHint: "Essayez d'ajuster les filtres.",
        totalCount_one: "{{count}} objectif",
        totalCount_other: "{{count}} objectifs",
        tableAriaLabel: "Objectifs de l'équipe",
        columns: {
            title: "Objectif",
            dueDate: "Échéance",
            status: "Statut",
            progress: "Progression",
            lastUpdatedAt: "Mis à jour"
        },
        liveRegion: {
            loading: "Chargement des objectifs",
            loadingMoreAnnouncement: "Chargement d'autres objectifs",
            loadedMore_one: "{{count}} objectif supplémentaire chargé",
            loadedMore_other: "{{count}} objectifs supplémentaires chargés",
            results_one: "{{count}} résultat",
            results_other: "{{count}} résultats",
            sortChanged: "Trié par {{column}} {{direction}}",
            ascending: "ordre croissant",
            descending: "ordre décroissant"
        }
    },
    TeamGoalDetailPage: {
        editGoal: "Modifier l’objectif",
        draft: "Brouillon",
        archived: "Archivé",
        liveRegion: {
            loading: "Chargement de l'objectif"
        },
        type: {
            organization: "Objectif d'organisation",
            team: "Objectif d'équipe",
            personal: "Objectif individuel"
        },
        sidebar: {
            owner: "Responsable",
            deletedMember: "Membre supprimé",
            unknownTeam: "Équipe inconnue",
            entireOrganization: "Toute l’organisation",
            teamKind: "Équipe",
            directReportsOf: "Subordonnés directs de {{name}}",
            reportingLineOf: "Ligne hiérarchique de {{name}}",
            createdBy: "Créé par {{name}}",
            contributors_one: "Contributeur ({{count}})",
            contributors_other: "Contributeurs ({{count}})",
            showAllContributors_one: "Afficher tous les contributeurs ({{count}})",
            showAllContributors_other: "Afficher tous les contributeurs ({{count}})",
            showLessContributors: "Afficher moins",
            parentGoal: "Objectif parent",
            startDate: "Date de début",
            due: "Échéance",
            visibility: "Visibilité"
        },
        parentScope: {
            organization: "Organisation",
            team: "Équipe",
            personal: "Individuel"
        },
        due: {
            dueIn_one: "dans {{count}} jour",
            dueIn_other: "dans {{count}} jours",
            today: "Aujourd'hui",
            overdue: "En retard"
        },
        visibility: {
            allOrganization: "Visible par l'organisation",
            ownerAndContributorsOnly: "Visible uniquement par le responsable et les contributeurs"
        },
        description: {
            heading: "Description"
        },
        progress: {
            heading: "Progression",
            percent: "{{value}} %",
            ariaLabel: "Progression de l'objectif {{title}}"
        },
        status: {
            onTrack: "Sur la bonne voie",
            atRisk: "À risque",
            offTrack: "Hors piste",
            noStatus: "Sans statut"
        },
        trackingMethod: {
            simple: "Progression simple — un seul pourcentage, mis à jour manuellement",
            measurements: "Mesures — moyenne pondérée des mesures ci-dessous",
            subGoals: "Sous-objectifs — mis à jour manuellement, en fonction des sous-objectifs ci-dessous",
            subGoalsAutomatic: "Mise à jour automatique depuis les sous-objectifs — recalculée dès qu'un sous-objectif change"
        },
        breadcrumb: {
            ariaLabel: "Fil d'Ariane",
            goals: "Objectifs"
        }
    },
    TeamGoalNotFound: {
        heading: "Objectif introuvable",
        body: "Cet objectif n'existe pas ou vous n'y avez pas accès.",
        backToGoals: "Retour aux objectifs"
    },
    GoalMeasurementRow: {
        valuePair: "{{current}} → {{target}}",
        boolean: {
            completed: "Complété",
            incomplete: "Non complété"
        },
        progressAriaLabel: "Progression de {{title}}",
        progressPercent: "{{value}} %"
    },
    GoalUpdates: {
        heading: "Mises à jour",
        noUpdates: "Aucune mise à jour publiée.",
        showAll_one: "Afficher toutes les mises à jour ({{count}})",
        showAll_other: "Afficher toutes les mises à jour ({{count}})",
        showLess: "Afficher moins"
    },
    GoalUpdateCard: {
        relativeTime: {
            today: "aujourd'hui",
            days_one: "il y a {{count}} jour",
            days_other: "il y a {{count}} jours"
        },
        overallProgress: "Progression globale",
        progressTransition: "{{previous}} % → {{current}} %",
        progressDelta: "({{delta}} %)",
        progressValue: "{{value}} %",
        measurementTransition: "{{previous}} → {{current}}",
        status: {
            onTrack: "Sur la bonne voie",
            atRisk: "À risque",
            offTrack: "Hors piste",
            noStatus: "Sans statut"
        }
    },
    GoalProgressUpdate: {
        addUpdate: "Mettre à jour le progrès",
        actions: {
            ariaLabel: "Actions pour la mise à jour de {{author}}",
            automaticAuthor: "cette mise à jour automatique",
            edit: "Modifier",
            delete: "Supprimer"
        },
        status: {
            onTrack: "Sur la bonne voie",
            atRisk: "À risque",
            offTrack: "Hors piste",
            noStatus: "Sans statut"
        },
        addedToast: "Mise à jour de progrès ajoutée",
        addErrorToast: "Erreur lors de l’ajout de la mise à jour de progrès",
        editedToast: "Mise à jour de progrès réussie",
        editErrorToast: "Erreur lors de la mise à jour du progrès",
        deletedToast: "Mise à jour de progrès supprimée",
        deleteErrorToast: "Désolé, une erreur s'est produite. Veuillez réessayer.",
        dialog: {
            addTitle: "Mettre à jour le progrès",
            editTitle: "Modifier le progrès",
            statusLabel: "Statut",
            progressLabel: "Progrès de l'objectif",
            progressPercent: "{{value}} %",
            progressInvalid: "Entrez un nombre entre 0 et 100.",
            progressFromMeasurements: "Calculé à partir des mesures ci-dessus. Saisissez une valeur pour le définir vous-même.",
            progressFromSubgoals: "La mise à jour automatique des sous-objectifs est activée. Le progrès ne peut pas être mis à jour manuellement.",
            loadingAriaLabel: "Chargement de l'objectif",
            measurementsLabel: "Mesures",
            measurementTarget: "Cible : {{target}}",
            measurementInvalid: "Entrez un nombre.",
            commentLabel: "Commentaire",
            commentPlaceholder: "Ajoutez un commentaire sur cette mise à jour de progrès...",
            cancel: "Annuler",
            addSubmit: "Enregistrer",
            editSubmit: "Enregistrer les modifications"
        },
        delete: {
            title: "Supprimer la mise à jour de progrès ?",
            body: "Cette mise à jour de progrès sera supprimée. Cette action ne peut pas être annulée.",
            confirm: "Supprimer",
            cancel: "Annuler"
        },
        discard: {
            title: "Abandonner cette mise à jour ?",
            body: "Vous n'avez pas encore enregistré ceci. Quitter maintenant fera perdre ce que vous avez écrit.",
            confirm: "Abandonner",
            cancel: "Continuer à modifier"
        }
    },
    GoalSubgoals: {
        deletedMember: "Membre supprimé",
        heading_one: "Sous-objectif ({{count}})",
        heading_other: "Sous-objectifs ({{count}})",
        type: {
            personal: "Objectif individuel",
            team: "Objectif d'équipe",
            organization: "Objectif d'organisation"
        },
        status: {
            onTrack: "Sur la bonne voie",
            atRisk: "À risque",
            offTrack: "Hors piste",
            noStatus: "Sans statut"
        },
        progressAriaLabel: "Progression de l'objectif {{title}}",
        progressPercent: "{{value}} %",
        dueLabel: "Échéance {{date}}",
        latestUpdate: {
            heading: "Dernière mise à jour",
            noUpdates: "Aucune mise à jour publiée.",
            today: "aujourd'hui",
            days_one: "il y a {{count}} jour",
            days_other: "il y a {{count}} jours",
            weeks_one: "il y a {{count}} semaine",
            weeks_other: "il y a {{count}} semaines"
        }
    },
    TeamReviewWritePage: {
        untitledCycle: "Évaluation",
        breadcrumb: { ariaLabel: "Fil d’Ariane", team: "Équipe", reviews: "Évaluations", reviewType: { manager: "Évaluation du gestionnaire", self: "Auto-évaluation", peer: "Évaluation par les pairs", upward: "Évaluation ascendante" } },
        cycleDescription: { expand: "Afficher plus", collapse: "Afficher moins" },
        perspectives: { yours: "Votre évaluation", manager: "Gestionnaire", self: "Auto-évaluation", peer: "Pairs", upward: "Ascendante" },
        assistant: {
            welcomeMessage: "Bonjour {{reviewerName}}, commençons l'évaluation de {{revieweeName}}",
            welcomeMessageSelf: "Bonjour {{reviewerName}}, commençons votre évaluation",
            subtext: {
                role: "actuellement {{jobTitle}}",
                team: "{{name}} fait partie de l'équipe {{department}}",
                teamTenure_one: "{{name}} est chez {{company}} depuis {{count}} an, dans l'équipe {{department}}",
                teamTenure_other: "{{name}} est chez {{company}} depuis {{count}} ans, dans l'équipe {{department}}",
                teamTenureLessThanYear: "{{name}} est chez {{company}} depuis moins d'un an, dans l'équipe {{department}}",
                tenure_one: "{{name}} est chez {{company}} depuis {{count}} an",
                tenure_other: "{{name}} est chez {{company}} depuis {{count}} ans",
                tenureLessThanYear: "{{name}} est chez {{company}} depuis moins d'un an",
                selfTenure_one: "{{count}} an chez {{company}}",
                selfTenure_other: "{{count}} ans chez {{company}}",
                selfTenureLessThanYear: "Moins d'un an chez {{company}}"
            },
            inputPlaceholder: "Demandez-moi de préparer ou de rédiger une réponse…",
            initialSuggestion: "Aide-moi à préparer l'évaluation"
        },
        loading: "Chargement de l'évaluation",
        progress: "{{answered}} réponse(s) sur {{total}}",
        autoSave: { saving: "Enregistrement…", saved: "Enregistré", error: "Échec de l'enregistrement — nouvelle tentative" },
        submit: "Soumettre l'évaluation",
        submitBlocked: "Répondez à toutes les questions obligatoires pour soumettre",
        submitSucceeded: "Évaluation soumise",
        submitFailed: "Impossible de soumettre l'évaluation",
        submitBlockedUnsaved: "Votre dernière réponse n'a pas pu être enregistrée, l'évaluation n'a donc pas été soumise. Nous réessayons — retentez dans un instant.",
        cancel: "Annuler",
        update: "Mettre à jour l'évaluation",
        updateSucceeded: "Évaluation mise à jour",
        unsavedChanges: "Vous avez des modifications non enregistrées",
        saveFailed: "Vos modifications n'ont pas pu être enregistrées. Elles sont toujours là — réessayez.",
        assistantUpdated: "Workleap AI a mis à jour cette évaluation",
        discard: {
            heading: "Quitter sans enregistrer?",
            body: "Vos modifications à cette évaluation n'ont pas été enregistrées. Quitter maintenant les supprimera.",
            keepEditing: "Continuer l'édition",
            discard: "Supprimer les modifications"
        },
        showAnswersInline: "Afficher les réponses en contexte",
        question: {
            optional: "Facultative",
            textPlaceholder: "Rédigez votre réponse…",
            ratingLabel: "Note",
            choiceLabel: "Choix",
            unlabeledValue: "Niveau {{value}}",
            inlineSelectedBy: "Aussi sélectionné par {{names}}",
            notInYourReview: "Ne fait pas partie de votre évaluation"
        },
        context: {
            title: "Contexte",
            open: "Afficher le contexte",
            close: "Masquer le contexte",
            workleapGroup: "Workleap",
            externalGroup: "Externe",
            emptyGroup: "Rien pour l'instant",
            progress: "{{value}} %",
            showing: "{{shown}} élément(s) sur {{total}}",
            viewAll: "Tout afficher ({{count}})",
            back: "Retour",
            sourcesCount: "{{count}} sources",
            sourceFrom: "provenant de {{source}}",
            sourceTypes: {
                Role: "Rôle",
                Goals: "Objectifs",
                CurrentReviews: "Évaluations en cours",
                PastReviews: "Évaluations précédentes",
                Recognition: "Good Vibes",
                Agendas: "1on1s",
                Engagement: "Engagement",
                Feedback: "Rétroaction"
            },
            visibility: {
                public: "Publique",
                direct: "Directe"
            }
        }
    },
    ReviewDetailPage: {
        liveRegion: {
            loading: "Chargement de l'évaluation"
        },
        empty: {
            heading: "Cette évaluation n'a pas encore commencé",
            hint: "Les réponses apparaîtront ici dès que l'évaluation sera en cours."
        },
        breadcrumb: {
            ariaLabel: "Fil d'Ariane",
            team: "Équipe",
            reviews: "Évaluations"
        }
    },
    ReviewDetailHeader: {
        untitledCycle: "Évaluation de performance",
        covers: "Couvre {{period}}",
        visibility: {
            reportingline: "Visible par le gestionnaire immédiat de {{name}} et ses supérieurs"
        },
        acknowledgedBy: "Prise de connaissance par {{name}} · {{date}}",
        sharedBy: "Partagée par {{name}} · {{date}}"
    },
    ReviewSharePreview: {
        pageTitle: "Aperçu de l'évaluation partagée",
        visibilityListHeader: "Voici la vue qui sera partagée avec {{name}}. Cela inclut :",
        types: {
            manager: "Les réponses du gestionnaire auxquelles il a accès",
            self: "Les réponses de son auto-évaluation",
            peerAnonymous: "Les réponses des pairs auxquelles il a accès (anonymisées)",
            peerWithNames: "Les réponses des pairs auxquelles il a accès (avec les noms visibles)",
            upwardAnonymous: "Les réponses des évaluations ascendantes auxquelles il a accès (anonymisées)",
            upwardWithNames: "Les réponses des évaluations ascendantes auxquelles il a accès (avec les noms visibles)"
        },
        editsVisible: "Toute modification effectuée après le partage sera également visible.",
        acknowledgmentWarning: "Ce cycle exige une prise de connaissance, vous ne pourrez donc plus retirer le partage une fois celui-ci effectué.",
        cancel: "Annuler",
        share: "Partager"
    },
    ReviewDetailActions: {
        actions: "Actions de l'évaluation",
        preparing: "Préparation…",
        manage: "Gérer l'évaluation"
    },
    ReviewExportAction: {
        exportPdf: "Exporter en PDF",
        preparing: "Préparation…",
        fullVersion: "Version complète",
        fullVersionDescription: "Toutes les questions et réponses de cette évaluation.",
        revieweeVersion: "Version de la personne évaluée",
        revieweeVersionDescription: "Uniquement ce que la personne évaluée peut voir. Les questions qui lui sont masquées sont exclues.",
        successToast: "Le fichier PDF a bien été exporté.",
        errorToast: "L'export a échoué. Veuillez réessayer."
    },
    ReviewReleaseActions: {
        previewAndShare: "Prévisualiser et partager",
        editReview: "Modifier l'évaluation",
        reviewType: {
            manager: "Évaluation du gestionnaire",
            self: "Auto-évaluation",
            peer: "Évaluation par les pairs",
            upward: "Évaluation ascendante"
        },
        shareDisabledReason: "Cette évaluation ne peut pas être partagée car toutes les questions sont masquées pour la personne évaluée.",
        unshare: "Retirer le partage",
        acknowledge: "Prendre connaissance",
        cancel: "Annuler",
        unshareDialog: {
            title: "Retirer le partage de cette évaluation?",
            description: "{{name}} perdra l'accès aux commentaires du gestionnaire et des pairs pour cette évaluation jusqu'à ce qu'elle soit partagée à nouveau.",
            confirm: "Retirer le partage"
        },
        acknowledgeDialog: {
            title: "Prendre connaissance de cette évaluation?",
            description: "Confirmer que vous avez pris connaissance de cette évaluation. Votre nom et la date seront enregistrés sur l'évaluation comme preuve de réception.",
            confirmation: "Je confirme avoir lu cette évaluation",
            confirm: "Prendre connaissance"
        },
        shareSuccessToast: "Vous avez partagé cette évaluation",
        unshareSuccessToast: "Vous avez retiré le partage de cette évaluation",
        errorToast: "Désolé, une erreur s'est produite. Veuillez réessayer."
    },
    ParticipantCard: {
        kind: {
            reviewee: "Personne évaluée",
            manager: "Gestionnaire",
            peer: "Pair",
            upward: "Ascendante"
        },
        anonymousName: {
            peer: "Pair anonyme",
            upward: "Évaluateur ascendant anonyme"
        },
        managerRole: {
            direct: "Gestionnaire immédiat",
            temporary: "gestionnaire immédiat temporaire",
            previous: "gestionnaire immédiat précédent"
        },
        status: {
            notstarted: "Non commencée",
            inprogress: "En cours",
            submitted: "Soumise"
        },
        submittedOn: "le {{date}}",
        dueOn: "Échéance {{date}}",
        deletedMember: "Membre supprimé",
        noDirectManager: "Pas de gestionnaire immédiat"
    },
    ReviewQuestionCard: {
        badges: {
            noAnswer: "Aucune réponse",
            optional: "Facultative"
        },
        emptyAnswer: {
            text: "Aucune réponse pour l'instant.",
            rating: "Aucune note pour l'instant.",
            choice: "Aucune sélection pour l'instant."
        },
        reviewerKind: {
            manager: "Évaluation du gestionnaire",
            self: "Auto-évaluation",
            peer: "Évaluation du pair",
            upward: "Évaluation ascendante"
        },
        anonymousReviewer: "Évaluateur anonyme",
        deletedMember: "Membre supprimé",
        ratingUnlabeledValue: "Niveau {{value}}",
        offScaleAnswers: "Réponses hors de l'échelle actuelle",
        selectedBy: "Choisi par {{names}}"
    },
    ReviewDetailNotFound: {
        heading: "Évaluation introuvable",
        body: "Cette évaluation n'existe pas, ou vous n'y avez pas accès.",
        backToReviews: "Retour aux évaluations"
    },
    ReviewSummary: {
        eyebrow: "Résumé IA",
        disclaimer: "Ce résumé a été généré par l'IA et peut contenir des inexactitudes. Consultez-le avec les réponses complètes ci-dessous.",
        errorToast: "Une erreur est survenue. Veuillez réessayer.",
        callout: {
            heading: "Besoin d'un aperçu rapide?",
            body: "Générez un résumé IA pour voir les principaux thèmes de toutes les évaluations soumises, au même endroit.",
            generate: "Générer un résumé",
            generating: "Génération du résumé…",
            noTextQuestions: "Un résumé ne peut pas être généré, car ce cycle d'évaluation ne contient aucune question de type texte."
        },
        menu: {
            triggerAriaLabel: "Actions du résumé",
            regenerate: "Régénérer",
            dismiss: "Fermer"
        },
        liveRegion: {
            loading: "Chargement du résumé de l'évaluation…"
        }
    },
    EngagementMetricDetailPage: {
        liveRegion: {
            loading: "Chargement des détails de la métrique d'engagement…"
        },
        questionsTableAriaLabel: "Questions de {{metric}}",
        timeFrame: {
            relativeLabel: {
                "7d": "7 jours",
                "30d": "30 jours",
                "3m": "3 mois",
                "6m": "6 mois",
                quarter: "ce trimestre",
                year: "cette année",
                allTime: "depuis toujours"
            }
        }
    },
    EngagementMetricDetailHeader: {
        breadcrumb: {
            ariaLabel: "Fil d'Ariane",
            engagement: "Retour à l'engagement"
        },
        tooltipLabel: "À propos de {{metric}}"
    },
    EngagementMetricDetailToolbar: {
        timeFrame: {
            ariaLabel: "Période",
            "7d": "7 derniers jours",
            "30d": "30 derniers jours",
            "3m": "3 derniers mois",
            "6m": "6 derniers mois",
            quarter: "Ce trimestre",
            year: "Cette année",
            allTime: "Depuis toujours"
        }
    },
    ScopeSegmentsSection: {
        searchPlaceholder: "Rechercher des segments enregistrés",
        header: "Segments enregistrés",
        error: "Impossible de charger les segments.",
        retry: "Réessayer",
        loadMore: "Chargement d'autres segments",
        empty: "Aucun segment enregistré pour l'instant.",
        noResults: "Aucun segment ne correspond à votre recherche.",
        star: "Marquer {{name}} comme favori",
        unstar: "Retirer {{name}} des favoris",
        starError: "Impossible de mettre à jour ce segment. Réessayez.",
        addFilter: "Ajouter un filtre",
        manageSegments: "Gérer les segments",
        liveRegion: {
            loadingSegments: "Chargement des segments enregistrés",
            loadedMore_one: "{{count}} segment supplémentaire chargé",
            loadedMore_other: "{{count}} segments supplémentaires chargés",
            results_one: "{{count}} segment",
            results_other: "{{count}} segments"
        }
    },
    EngagementMetricDetailNotFound: {
        heading: "Métrique introuvable",
        body: "Cette métrique d'engagement n'existe pas ou vous n'y avez pas accès.",
        backToEngagement: "Retour à l'engagement"
    },
    TeamFilterPopover: {
        trigger: "Ajouter un filtre",
        dialogTitle: "Appliquer des filtres",
        loading: "Chargement des champs des employés",
        error: "Impossible de charger vos champs d'employés.",
        retry: "Réessayer",
        matchCount_one: "{{count}} personne correspond",
        matchCount_other: "{{count}} personnes correspondent",
        countLoading: "Vérification des correspondances",
        matchTypeFixed: "La correspondance à au moins une règle n'est pas offerte ici.",
        matchAnySegment: "Ce segment correspond à au moins une de ses règles, il ne peut donc pas être modifié ici. Il s'applique toujours exactement comme il a été enregistré.",
        saveAsSegment: "Enregistrer comme segment",
        applyFilters: "Appliquer les filtres"
    },
    TeamFilterActions: {
        appliedRulesLabel: "Filtres appliqués",
        saveAsSegment: "Enregistrer comme segment",
        clearFilters: "Effacer les filtres"
    },
    SaveAsSegmentDialog: {
        title: "Enregistrer comme segment",
        back: "Retour aux filtres",
        name: {
            label: "Nom",
            placeholder: "Ingénierie à Toronto",
            required: "Entrez un nom pour ce segment.",
            tooLong: "Limitez le nom à moins de {{count}} caractères."
        },
        save: "Enregistrer le segment",
        cancel: "Annuler",
        createdToast: "Segment créé",
        saveErrorToast: "Impossible d'enregistrer ce segment. Réessayez."
    },
    ManageSegmentsModal: {
        title: "Gérer les segments"
    },
    ManageSegmentsSidebar: {
        heading: "Segments",
        searchPlaceholder: "Rechercher un segment",
        listAriaLabel: "Segments",
        newSegment: "Nouveau segment",
        mySegments: "Mes segments",
        sharedWithMe: "Partagés avec moi",
        sharedBy: "Partagé par {{name}}",
        applied: "Appliqué",
        currentFilters: "Filtres actuels",
        currentFiltersCaption_one: "{{count}} règle",
        currentFiltersCaption_other: "{{count}} règles",
        empty: "Aucun segment pour l'instant.",
        noResults: "Aucun segment ne correspond à votre recherche.",
        loadError: "Impossible de charger les segments.",
        retry: "Réessayer",
        loadMore: "Chargement d'autres segments",
        liveRegion: {
            loading: "Chargement des segments",
            loadedMore_one: "{{count}} segment supplémentaire chargé",
            loadedMore_other: "{{count}} segments supplémentaires chargés",
            results_one: "{{count}} segment",
            results_other: "{{count}} segments"
        }
    },
    ManageSegmentsEditorPanel: {
        emptyTitle: "Aucun segment sélectionné",
        emptyDescription: "Sélectionnez un segment à modifier, ou créez-en un nouveau.",
        loading: "Chargement du segment",
        loadError: "Impossible de charger ce segment.",
        retry: "Réessayer",
        notFoundTitle: "Ce segment n'existe pas, ou il n'est pas partagé avec vous.",
        backToList: "Retour aux segments"
    },
    TeamSegmentForm: {
        moreActionsAriaLabel: "Plus d'actions sur le segment",
        sharedPill: "Partagé",
        matchTypeFixed: "La correspondance à au moins une règle n'est pas offerte ici.",
        apply: "Appliquer",
        saveAsSegment: "Enregistrer comme segment",
        saveAsNew: "Enregistrer comme nouveau",
        copyName: "Copie de {{name}}",
        saveChanges: "Enregistrer les modifications",
        deleteConfirm: {
            title: "Supprimer « {{name}} »?",
            description: "Le segment sera retiré pour toutes les personnes avec qui il est partagé. Les personnes qui en font partie et leurs données ne sont pas touchées. Cette action est irréversible.",
            cancel: "Annuler",
            confirm: "Supprimer"
        }
    },
    CurrentFiltersForm: {
        title: "Filtres actuels",
        matchTypeFixed: "La correspondance à au moins une règle n'est pas offerte ici.",
        saveAsSegment: "Enregistrer comme segment",
        apply: "Appliquer"
    }
};
